import { useCallback, useContext, useMemo } from 'react';

import {
  usePanelNavigation,
  usePanelPreferenceStore,
  usePanelScopeContext,
  usePanelScopeRegistry,
  useViewMode,
} from '@kc-console/context';

import { SplitDetailPanelStoreContext } from '../context';

import type { ComponentType, MouseEvent } from 'react';
import type { PanelComponent, ResourceItem, SplitDetailPanelActions, TabKey } from '../types';

// ============================================================================
// Types
// ============================================================================

/**
 * 탭 패널 옵션 (Detail 화면 전용)
 */
export interface TabPanelOptions<T extends ResourceItem = ResourceItem> {
  /**
   * 패널에 전달할 아이템 데이터
   */
  item: T;

  /**
   * 리소스 타입 (panelComponent에서 컴포넌트 조회에 사용)
   * @example 'record', 'subnet', 'user'
   */
  resourceType: string;

  /**
   * 탭 활성화 여부 (기본값: true)
   * - true: 새 탭으로 전환
   * - false: 백그라운드에 탭만 추가
   */
  activate?: boolean;

  /**
   * 패널 컴포넌트 (옵셔널 - 없으면 Registry에서 조회)
   */
  component?: PanelComponent<T>;

  /**
   * 탭 라벨 (선택)
   */
  label?: string;
}

/**
 * usePanelActions 반환 타입
 */
export interface UsePanelActionsReturn {
  /**
   * 모든 탭 패널 닫기
   * @param resourceType - 특정 타입만 닫을 경우 지정
   */
  closeAllTabPanels: (resourceType?: string) => void;

  /**
   * 현재 scope의 패널을 닫는 함수
   */
  closePanel: () => void;

  /**
   * 탭 패널 닫기
   */
  closeTabPanel: (key: TabKey) => void;

  // 상태
  /**
   * DetailPanelProvider 하위 여부 (탭 시스템 사용 가능 여부)
   */
  isTabMode: boolean;

  // Selection 기반 (List 화면)
  /**
   * 패널을 직접 여는 함수
   */
  openPanel: (panelId: string) => void;

  /**
   * 클릭 이벤트와 함께 패널을 여는 함수
   * - metaKey/ctrlKey: 새 브라우저 탭 열기 허용
   * - embedded 모드: 동작 안 함
   */
  openPanelByClick: (event: MouseEvent, panelId: string) => void;

  // 탭 기반 (Detail 화면)
  /**
   * 탭 패널을 직접 여는 함수
   */
  openTabPanel: <T extends ResourceItem>(options: TabPanelOptions<T>) => void;

  /**
   * 클릭 이벤트와 함께 탭 패널을 여는 함수
   */
  openTabPanelByClick: <T extends ResourceItem>(
    event: MouseEvent,
    options: TabPanelOptions<T>,
  ) => void;

  /**
   * 패널 열기 가능 여부
   * - embedded 모드에서는 false
   * - Provider 외부에서는 false
   */
  shouldOpenPanel: boolean;

  /**
   * 탭 패널 전환
   */
  switchTabPanel: (key: TabKey) => void;

  /**
   * 현재 viewMode ('standalone' | 'embedded')
   */
  viewMode: 'standalone' | 'embedded';
}

// ============================================================================
// Internal Helpers
// ============================================================================

/**
 * Optional 버전의 탭 액션 접근
 * Provider가 없으면 null을 반환합니다.
 */
function useTabActionsOptional(): SplitDetailPanelActions | null {
  const context = useContext(SplitDetailPanelStoreContext);

  if (!context) {
    return null;
  }

  const state = context.store.getState();
  return {
    closeAllTabs: state.closeAllTabs,
    closeTab: state.closeTab,
    openTab: state.openTab,
    reconcileTabs: state.reconcileTabs,
    reset: state.reset,
    setVisible: state.setVisible,
    switchTab: state.switchTab,
  };
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * 패널 시스템의 유일한 Public API
 *
 * ListPanelProvider 또는 DetailPanelProvider 하위에서 사용해야 합니다.
 * Provider 외부에서 사용하면 개발 모드에서 에러를 throw합니다.
 *
 * @example Selection 기반 (List 화면)
 * ```tsx
 * const { openPanelByClick } = usePanelActions();
 *
 * <RouterLink
 *   href={`/dns/zones/${zone.id}`}
 *   onClick={e => openPanelByClick(e, zone.id)}
 * >
 *   {zone.name}
 * </RouterLink>
 * ```
 *
 * @example 탭 기반 (Detail 화면)
 * ```tsx
 * const { openTabPanelByClick } = usePanelActions();
 *
 * <RouterLink
 *   href={`/dns/zones/${zoneId}/records/${record.id}`}
 *   onClick={e => openTabPanelByClick(e, {
 *     resourceType: 'record',
 *     item: record,
 *     label: record.name,
 *   })}
 * >
 *   {record.name}
 * </RouterLink>
 * ```
 */
export function usePanelActions(): UsePanelActionsReturn {
  // Context에서 scopeKey 자동 획득
  const panelScope = usePanelScopeContext();
  const scopeKey = panelScope?.scopeKey;

  // 내부 stores/hooks
  const viewMode = useViewMode();
  const tabActions = useTabActionsOptional();
  const closePanel = usePanelNavigation(state => state.closePanel);
  // Phase 6: openDetailPanel → openSidePanel 리네이밍
  const openSidePanel = usePanelNavigation(state => state.openSidePanel);
  const getScopeConfig = usePanelScopeRegistry(state => state.getScopeConfig);
  const preferences = usePanelPreferenceStore(state => state.preferences.detail);
  const availability = usePanelPreferenceStore(state => state.availability.detail);

  const isTabMode = tabActions !== null;

  // shouldOpenPanel 계산
  const shouldOpenPanel = useMemo(() => {
    // embedded 모드에서는 패널 생성 차단
    if (viewMode === 'embedded') return false;
    // Provider 외부에서는 패널 생성 불가
    if (!scopeKey) return false;
    // Preference/Availability 확인
    return preferences.enabled && availability;
  }, [viewMode, scopeKey, preferences.enabled, availability]);

  // 에러 처리 헬퍼 (개발 모드에서 에러 throw)
  const assertScopeKey = useCallback((): boolean => {
    if (!scopeKey) {
      if (process.env.NODE_ENV === 'development') {
        throw new Error(
          '[usePanelActions] Must be used within ListPanelProvider or DetailPanelProvider',
        );
      }
      return false;
    }
    return true;
  }, [scopeKey]);

  // ========================================================================
  // Selection 기반 API
  // ========================================================================

  /**
   * 패널을 직접 여는 함수
   */
  const handleOpenPanel = useCallback(
    (panelId: string) => {
      if (!assertScopeKey() || !scopeKey) return;

      const config = getScopeConfig(scopeKey);
      if (!config?.panelResource) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[usePanelActions] No panelResource for scope: ${scopeKey}`);
        }
        return;
      }

      if (!shouldOpenPanel) return;

      // Phase 6: openDetailPanel → openSidePanel 리네이밍
      openSidePanel({
        scopeKey,
        panelResource: config.panelResource,
        panelId,
        viewMode: 'standalone',
      });
    },
    [assertScopeKey, scopeKey, getScopeConfig, shouldOpenPanel, openSidePanel],
  );

  /**
   * 클릭 이벤트와 함께 패널을 여는 함수
   */
  const handleOpenPanelByClick = useCallback(
    (event: MouseEvent, panelId: string) => {
      // metaKey/ctrlKey가 눌린 경우 새 탭 열기 허용
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      // embedded 모드에서는 기본 링크 동작 유지
      if (viewMode === 'embedded') {
        return;
      }

      if (!assertScopeKey() || !scopeKey) return;

      const config = getScopeConfig(scopeKey);
      if (!config?.panelResource) {
        if (process.env.NODE_ENV === 'development') {
          console.warn(`[usePanelActions] No panelResource for scope: ${scopeKey}`);
        }
        return;
      }

      // 패널을 열 수 없는 상태면 기본 링크 동작 유지
      if (!shouldOpenPanel) {
        return;
      }

      // 기본 동작 방지 후 패널 열기
      // Phase 6: openDetailPanel → openSidePanel 리네이밍
      event.preventDefault();
      openSidePanel({
        scopeKey,
        panelResource: config.panelResource,
        panelId,
        viewMode: 'standalone',
      });
    },
    [viewMode, assertScopeKey, scopeKey, getScopeConfig, shouldOpenPanel, openSidePanel],
  );

  /**
   * 현재 scope의 패널을 닫는 함수
   */
  const handleClosePanel = useCallback(() => {
    if (!assertScopeKey() || !scopeKey) return;
    closePanel(scopeKey);
  }, [assertScopeKey, scopeKey, closePanel]);

  // ========================================================================
  // 탭 기반 API
  // ========================================================================

  /**
   * 컴포넌트 조회 헬퍼
   * 우선순위: 직접 전달 > Registry[resourceType] > Registry.default
   */
  const resolveComponent = useCallback(
    <T extends ResourceItem>(
      options: TabPanelOptions<T>,
    ): ComponentType<{ item: T; onClose?: () => void }> | null => {
      // 1. 직접 전달된 컴포넌트
      if (options.component) {
        return options.component;
      }

      // 2. Registry에서 조회
      if (!scopeKey) return null;

      const config = getScopeConfig(scopeKey);
      const panelComponentMap = config?.panelComponent;

      if (!panelComponentMap || typeof panelComponentMap !== 'object') {
        return null;
      }

      // Record 타입인 경우에만 조회
      const componentMap = panelComponentMap as Record<string, ComponentType<any>>;

      // resourceType별 매핑 조회
      if (componentMap[options.resourceType]) {
        return componentMap[options.resourceType];
      }

      // default fallback
      if (componentMap.default) {
        return componentMap.default;
      }

      return null;
    },
    [scopeKey, getScopeConfig],
  );

  /**
   * 탭 패널을 직접 여는 함수
   */
  const handleOpenTabPanel = useCallback(
    <T extends ResourceItem>(options: TabPanelOptions<T>) => {
      if (!assertScopeKey() || !tabActions) return;

      const component = resolveComponent(options);

      if (!component) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error(
            `[usePanelActions] No component found for resourceType: ${options.resourceType}. ` +
              `Register it in panelComponent or pass component directly.`,
          );
        }
        return;
      }

      // embedded 모드에서는 동작 안 함
      if (viewMode === 'embedded') {
        return;
      }

      // 패널 설정이 OFF인 경우 동작 안 함
      if (!shouldOpenPanel) {
        return;
      }

      tabActions.openTab(
        options.resourceType,
        options.item,
        component as ComponentType<any>,
        options.activate ?? true,
        options.label,
      );
    },
    [assertScopeKey, tabActions, resolveComponent, viewMode, shouldOpenPanel],
  );

  /**
   * 클릭 이벤트와 함께 탭 패널을 여는 함수
   */
  const handleOpenTabPanelByClick = useCallback(
    <T extends ResourceItem>(event: MouseEvent, options: TabPanelOptions<T>) => {
      // metaKey/ctrlKey가 눌린 경우 새 탭 열기 허용
      if (event.metaKey || event.ctrlKey) {
        return;
      }

      // embedded 모드에서는 기본 링크 동작 유지
      if (viewMode === 'embedded') {
        return;
      }

      // 패널 설정이 OFF인 경우 기본 링크 동작 유지
      if (!shouldOpenPanel) {
        return;
      }

      if (!assertScopeKey() || !tabActions) return;

      const component = resolveComponent(options);

      if (!component) {
        if (process.env.NODE_ENV === 'development') {
          throw new Error(
            `[usePanelActions] No component found for resourceType: ${options.resourceType}. ` +
              `Register it in panelComponent or pass component directly.`,
          );
        }
        return;
      }

      // 기본 동작 방지 후 탭 열기
      event.preventDefault();
      tabActions.openTab(
        options.resourceType,
        options.item,
        component as ComponentType<any>,
        options.activate ?? true,
        options.label,
      );
    },
    [viewMode, shouldOpenPanel, assertScopeKey, tabActions, resolveComponent],
  );

  /**
   * 탭 패널 닫기
   */
  const handleCloseTabPanel = useCallback(
    (key: TabKey) => {
      tabActions?.closeTab(key);
    },
    [tabActions],
  );

  /**
   * 모든 탭 패널 닫기
   */
  const handleCloseAllTabPanels = useCallback(
    (resourceType?: string) => {
      tabActions?.closeAllTabs(resourceType);
    },
    [tabActions],
  );

  /**
   * 탭 패널 전환
   */
  const handleSwitchTabPanel = useCallback(
    (key: TabKey) => {
      tabActions?.switchTab(key);
    },
    [tabActions],
  );

  return {
    // Selection 기반
    openPanel: handleOpenPanel,
    openPanelByClick: handleOpenPanelByClick,
    closePanel: handleClosePanel,
    // 탭 기반
    openTabPanel: handleOpenTabPanel,
    openTabPanelByClick: handleOpenTabPanelByClick,
    closeTabPanel: handleCloseTabPanel,
    closeAllTabPanels: handleCloseAllTabPanels,
    switchTabPanel: handleSwitchTabPanel,
    // 상태
    isTabMode,
    viewMode,
    shouldOpenPanel,
  };
}
